import { Stripe, StripeElements } from "@stripe/stripe-js";
import { useState } from "react";
import { usePaymentLinkPaymentIntentCreate } from "../../../hooks/api/v2/paymentLink/usePaymentLinkPaymentIntentCreate";

type PayResult = { success: true } | { success: false; error: string };

export const usePaymentLinkPayment = ({ code }: { code: string }) => {
  const paymentIntentCreate = usePaymentLinkPaymentIntentCreate(code);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const pay = async (
    stripe: Stripe,
    elements: StripeElements,
  ): Promise<PayResult> => {
    setError("");
    setLoading(true);

    try {
      // 1. Validate the Payment Element
      const { error: submitError } = await elements.submit();
      if (submitError) {
        const msg = submitError.message ?? "Payment validation failed";
        setError(msg);
        return { success: false, error: msg };
      }

      // 2. Create payment intent via backend
      const { clientSecret } = await paymentIntentCreate.mutateAsync({});

      // 3. Confirm payment with Stripe
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
        },
      });

      // If confirmPayment returns (no redirect), it means there was an error
      if (confirmError) {
        const msg = confirmError.message ?? "Payment failed. Please try again.";
        setError(msg);
        return { success: false, error: msg };
      }

      // Stripe will redirect on success, so we shouldn't reach here for card payments
      return { success: true };
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Something went wrong, please try again";
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  return { pay, loading, error, setError };
};
